import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { LoginService } from './login.service';
import { ClientService } from './client.service';
import { ClientActions } from '../store/client/client.actions';
import { TranslateService } from '@ngx-translate/core';
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from '../models/supported-language.model';

@Injectable({
  providedIn: 'root',
})
export class AppInitializationService {
  private loginService = inject(LoginService);
  private clientService = inject(ClientService);
  private router = inject(Router);
  private store = inject(Store);
  private translateService = inject(TranslateService);

  /**
   * Vérifie si l'utilisateur a des tokens valides et effectue la connexion automatique
   * @returns Promise<void>
   */
  async initializeApp(): Promise<void> {
    try {
      this.initializeLanguage();
      const isLoggedIn: Boolean = await this.checkAndAutoLogin();
      if (isLoggedIn) {
        console.info('Connexion automatique réussie');
      } else {
        console.warn('Aucune connexion automatique possible');
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'application:", error);
    }
  }

  /**
   * Initialise la langue de l'app
   * localStorage > navigateur > défaut (en-EN)
   */
  private initializeLanguage(): void {
    this.translateService.addLangs([...SUPPORTED_LANGUAGES]);

    const localStorageLang: string | null = localStorage.getItem('language');
    const navigatorLang: string = navigator.language;
    const isSupportedLanguage = (lang: string): lang is SupportedLanguage => {
      return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
    };
    let selectedLanguage: SupportedLanguage;

    if (localStorageLang && isSupportedLanguage(localStorageLang)) {
      selectedLanguage = localStorageLang;
    } else if (isSupportedLanguage(navigatorLang)) {
      selectedLanguage = navigatorLang;
    } else {
      selectedLanguage = DEFAULT_LANGUAGE;
    }
    this.translateService.setDefaultLang(selectedLanguage);
    this.translateService.use(selectedLanguage);
  }

  /**
   * Vérifie les tokens stockés et effectue la connexion automatique si possible
   * @returns Promise<boolean> - true si la connexion automatique a réussi
   */
  private async checkAndAutoLogin(): Promise<boolean> {
    const { token, refreshToken } = this.loginService.getStorageAccessToken();

    // Aucun token disponible
    if (!token && !refreshToken) {
      return false;
    }

    try {
      // Vérifier si le token principal est valide
      if (token && this.loginService.checkToken(token)) {
        await this.performAutoLogin(token);
        return true;
      }

      // Si le token principal est expiré, essayer avec le refresh token
      if (refreshToken && this.loginService.checkToken(refreshToken)) {
        const response = await this.loginService.refresh(refreshToken);
        this.loginService.setStorageAccessToken(
          response.token,
          response.ref_token
        );
        await this.performAutoLogin(response.token);
        return true;
      }

      // Aucun token valide, nettoyer le localStorage
      this.cleanupInvalidTokens();
      return false;
    } catch (error) {
      console.error('Erreur lors de la connexion automatique:', error);
      this.cleanupInvalidTokens();
      return false;
    }
  }

  /**
   * Effectue la connexion automatique en chargeant les données utilisateur
   * @param token - Token d'accès valide
   */
  private async performAutoLogin(token: string): Promise<void> {
    try {
      // Charger les informations utilisateur
      const client = await firstValueFrom(this.clientService.getClientData());

      // Dispatcher l'action de connexion dans le store
      this.store.dispatch(ClientActions.loginClient({ client }));
      //   console.log('Utilisateur connecté automatiquement:', client);
    } catch (error) {
      console.error(
        'Erreur lors du chargement des données utilisateur:',
        error
      );
      throw error;
    }
  }

  /**
   * Nettoie les tokens invalides du localStorage
   */
  private cleanupInvalidTokens(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }
}
